<template>
  <div class="evidence-status">
    <div :class="`evidence-banner banner-${config.variant}`">
      <div class="d-flex flex-row align-items-start">
        <div class="flex-grow-1">
          <div v-if="config.title" class="evidence-title">
            <span v-if="isPending" class="pulse-dot" />
            {{ config.title }}
            <i v-if="failureMessage" class="bi bi-info-circle ms-1" :title="failureMessage" style="cursor: help; font-weight: normal;" />
          </div>
          <div v-if="config.body" class="evidence-body">
            {{ config.body }}
          </div>
        </div>

        <!-- Captured: ellipsis menu -->
        <div v-if="isCaptured" class="dropdown flex-shrink-0">
          <button
            type="button"
            class="btn btn-sm btn-link text-dark p-0"
            data-bs-toggle="dropdown"
          >
            <i class="bi bi-three-dots" />
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            <li>
              <button type="button" class="dropdown-item d-flex align-items-center gap-2" :disabled="retrying || !listingId" @click="retry">
                <i class="bi bi-camera" /> Recapture
              </button>
            </li>
            <li>
              <button type="button" class="dropdown-item d-flex align-items-center gap-2" :disabled="!listingId" @click="showUploadModal = true">
                <i class="bi bi-upload" /> Upload new
              </button>
            </li>
            <li v-if="listingId">
              <EvidenceScreenshotButton
                :city-id="cityId"
                :listing-id="listingId"
                :evidence="evidence"
                class="dropdown-item d-flex align-items-center gap-2"
              />
            </li>
          </ul>
        </div>
      </div>

      <!-- No evidence: capture button -->
      <div class="d-flex flex-row align-items-center gap-2 mt-2" v-if="!effectiveEvidence && listingId">
        <button type="button" class="btn btn-sm btn-outline-dark d-flex flex-row align-items-center gap-1" :disabled="retrying" @click="retry">
          <i class="bi bi-camera" />
          Capture
        </button>
      </div>

      <!-- Failed: retry, upload -->
      <div class="d-flex flex-row align-items-center gap-2 mt-2" v-else-if="effectiveEvidence?.status === 'failed'">
        <button type="button" class="btn btn-sm btn-outline-dark d-flex flex-row align-items-center gap-1" :disabled="retrying || !listingId" @click="retry">
          <i class="bi bi-camera" />
          Retry
        </button>
        <button type="button" class="btn btn-sm btn-outline-dark d-flex flex-row align-items-center gap-1" :disabled="!listingId" @click="showUploadModal = true">
          <i class="bi bi-upload" />
          Upload
        </button>
      </div>

    </div>
  </div>

  <!-- Upload modal -->
  <ModalView
    v-model="showUploadModal"
    title="Upload Screenshot"
    hide-footer
  >
    <div class="d-flex flex-column gap-3">
      <a
        v-if="evidence?.sourceUrl"
        :href="safeHref(evidence.sourceUrl)"
        target="_blank"
        class="btn btn-outline-dark d-flex align-items-center justify-content-center gap-2"
       rel="noopener noreferrer">
        <i class="bi bi-box-arrow-up-right" />
        Visit listing to take a screenshot
      </a>
      <EvidenceUploadZone
        :city-id="cityId"
        :listing-id="listingId"
        @uploaded="onUploadComplete"
      />
    </div>
  </ModalView>
</template>

<script>
import { safeHref } from '@/safeRendering';
import { onSnapshot, doc } from 'firebase/firestore';
import { db } from '@/db';
import { postApi } from '@/api';
import ModalView from '@/components/common/ModalView.vue';
import EvidenceUploadZone from '@/components/letters/EvidenceUploadZone.vue';
import EvidenceScreenshotButton from './EvidenceScreenshotButton.vue';

export default {
  components: { ModalView, EvidenceUploadZone, EvidenceScreenshotButton },
  props: {
    evidence: { type: Object, default: null },
    cityId: { type: String, required: true },
    listingId: { type: String, required: true }
  },
  emits: ['updated'],
  data() {
    return {
      retrying: false,
      unsubscribe: null,
      showUploadModal: false,
      optimisticPending: false
    };
  },
  computed: {
    effectiveEvidence() {
      if (this.optimisticPending) return { status: 'pending' };
      return this.evidence;
    },
    config() {
      if (!this.effectiveEvidence) {
        return {
          variant: 'none',
          title: 'No screenshot captured',
          body: 'Letters can be created but cannot be printed until a screenshot is captured.'
        };
      } else if (this.effectiveEvidence.status === 'failed') {
        return {
          variant: 'failed',
          title: 'Screenshot capture failed',
          body: 'Screenshots cannot be attached to letters until a new capture succeeds or an image is uploaded manually.',
        };
      } else if (this.effectiveEvidence.status === 'pending') {
        return {
          variant: 'pending',
          title: 'Capturing screenshot…'
        };
      } else if (this.effectiveEvidence.status === 'skipped') {
        return {
          variant: 'skipped',
          title: 'Skipped screenshot capture'
        };
      } else if (this.isCaptured) {
        return {
          variant: 'captured',
          title: 'Screenshot captured',
          body: ''
        };
      }
      return null;
    },
    isCaptured() {
      return this.effectiveEvidence?.status === 'captured' || this.effectiveEvidence?.status === 'manual_upload';
    },
    isPending() {
      return this.effectiveEvidence?.status === 'pending';
    },
    failureMessage() {
      const reason = this.evidence?.failureReason;
      if (!reason) return null;
      if (typeof reason === 'string') return reason;
      if (typeof reason.message === 'string') return reason.message;
      if (typeof reason.error?.message === 'string') return reason.error?.message;
      return String(reason);
    }
  },
  watch: {
    evidence() {
      this.optimisticPending = false;
    },
    isPending: {
      immediate: true,
      handler(pending) {
        if (pending) {
          this.startListening();
        } else {
          this.stopListening();
        }
      }
    }
  },
  beforeUnmount() {
    this.stopListening();
  },
  methods: {
    safeHref,

    async startListening() {
      if (this.unsubscribe) return;
      if (!this.cityId || !this.listingId) return;

      const firestore = await db;
      const listingPath = `cities/${this.cityId}/listings/${this.listingId}`;
      const docRef = doc(firestore, listingPath);

      this.unsubscribe = onSnapshot(docRef, (snapshot) => {
        if (!snapshot.exists()) return;
        const data = snapshot.data();
        const newStatus = data?.evidence?.status;

        if (newStatus && newStatus !== 'pending') {
          this.$emit('updated', data.evidence);
          this.stopListening();
        }
      });
    },
    stopListening() {
      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }
    },
    async retry() {
      if (!this.cityId || !this.listingId) return;
      this.retrying = true;
      this.optimisticPending = true;
      try {
        await postApi(`/v1/cities/${this.cityId}/listings/${this.listingId}/evidence/retry`);
        this.$emit('updated', { status: 'pending' });
      } catch (err) {
        this.optimisticPending = false;
        console.error('Evidence retry failed:', err);
      } finally {
        this.retrying = false;
      }
    },
    onUploadComplete() {
      this.showUploadModal = false;
      this.$emit('updated');
    }
  }
};
</script>

<style scoped>
.pulse-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--bs-primary);
  vertical-align: middle;
  margin-right: 6px;
  animation: pulse 1.4s ease-in-out infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(0.75); }
}
</style>
